/**
 * <gh-carousel> — horizontal scroll-snap carousel (Gaucho House).
 *
 * Markup:
 *   <gh-carousel>
 *     …{% render 'gh-carousel-arrows' %}…          (optional: buttons with ref="previous" / ref="next")
 *     <div data-gh-carousel-track>                 (the scroller: overflow-x auto + scroll-snap)
 *       <article data-gh-carousel-slide>…</article>
 *     </div>
 *     {% render 'gh-mobile-carousel' %}            (optional: progress bar, mobile only)
 *   </gh-carousel>
 *
 * Swipe and trackpad scrolling are native. The arrows move one slide and are disabled at the
 * start and at the end of the scroll (measured from the scroll position, so partially visible
 * slides work). The progress bar shows one slide's share of the bar and slides along it as the
 * track scrolls.
 *
 * Mobile carousels (`data-gh-mobile-carousel` on the element) only scroll below 750px: there the
 * track and any slide without its own link or button become keyboard-focusable.
 */
const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');

class GhCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gh-carousel-track]');
    if (!this.track) return;

    this.previousButton = this.querySelector('[ref="previous"]');
    this.nextButton = this.querySelector('[ref="next"]');
    this.progress = this.querySelector('[data-gh-carousel-progress]');

    this.onPrevious = (event) => {
      event.preventDefault();
      this.step(-1);
    };
    this.onNext = (event) => {
      event.preventDefault();
      this.step(1);
    };
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };
    this.onBreakpoint = () => this.syncFocus();

    this.previousButton?.addEventListener('click', this.onPrevious);
    this.nextButton?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.hasAttribute('data-gh-mobile-carousel')) {
      MOBILE_QUERY.addEventListener('change', this.onBreakpoint);
      this.syncFocus();
    }

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);

    this.update();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    MOBILE_QUERY.removeEventListener('change', this.onBreakpoint);
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  get slides() {
    return [...this.track.querySelectorAll('[data-gh-carousel-slide]')];
  }

  step(direction) {
    const [first] = this.slides;
    if (!first) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = first.getBoundingClientRect().width + gap;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollBy({ left: direction * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    const atStart = Math.abs(scrollLeft) <= 2;
    const atEnd = Math.abs(scrollLeft) >= max - 2;

    if (this.previousButton) this.previousButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd || max <= 2;

    if (this.progress) {
      const count = Math.max(this.slides.length, 1);
      const ratio = max > 2 ? Math.min(Math.max(Math.abs(scrollLeft) / max, 0), 1) : 0;
      this.progress.style.setProperty('--gh-progress-count', count);
      this.progress.style.setProperty('--gh-progress', ratio.toFixed(4));
    }
  }

  /** Mobile carousels: the track and slides without their own control get a tab stop. */
  syncFocus() {
    const scrolls = MOBILE_QUERY.matches && getComputedStyle(this.track).overflowX !== 'visible';

    if (scrolls) {
      this.track.setAttribute('tabindex', '0');
    } else {
      this.track.removeAttribute('tabindex');
    }

    for (const slide of this.slides) {
      const hasControl = slide.querySelector('a[href], button:not([disabled]), input, select, textarea');
      if (scrolls && !hasControl) {
        slide.setAttribute('tabindex', '0');
        slide.dataset.ghFocusAdded = '';
      } else if ('ghFocusAdded' in slide.dataset) {
        slide.removeAttribute('tabindex');
        delete slide.dataset.ghFocusAdded;
      }
    }

    this.update();
  }
}

if (!customElements.get('gh-carousel')) customElements.define('gh-carousel', GhCarousel);
